import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/DeliveryStartCheck.css';

interface DriverInfo {
  id: number;
  username: string;
  name: string;
  phone?: string;
  is_admin?: boolean;
  assigned_apartments: string[];
  delivery_start_time?: string;  // 배송 출발 시간 (HH:MM)
  delivery_start_day_offset?: number;  // 0=당일, -1=전날
  delivery_start_end_time?: string;  // 배송 시작 마감 시간 (HH:MM)
}

const DeliveryStartCheck: React.FC = () => {
  const navigate = useNavigate();
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [confirmationTime, setConfirmationTime] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [canStart, setCanStart] = useState(false);
  const [deliveryStartTime, setDeliveryStartTime] = useState('02:00');
  const [deliveryStartDayOffset, setDeliveryStartDayOffset] = useState(0);
  const [deliveryStartEndTime, setDeliveryStartEndTime] = useState('10:00');
  const [secondsUntilStart, setSecondsUntilStart] = useState(0);
  const [secondsUntilEnd, setSecondsUntilEnd] = useState(0);
  const [unavailableReason, setUnavailableReason] = useState('');
  const [hasDelivery, setHasDelivery] = useState(false);
  const [deliveryApartments, setDeliveryApartments] = useState<string[]>([]);
  const [deliveryTotalCount, setDeliveryTotalCount] = useState(0);

  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    if (window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${hostname}:8000`;
  };

  useEffect(() => {
    // 로그인 정보 확인
    const token = localStorage.getItem('token');
    const savedDriver = localStorage.getItem('driverInfo');
    
    if (!token || !savedDriver) {
      navigate('/login');
      return;
    }
    
    const driver = JSON.parse(savedDriver);
    setDriverInfo(driver);
    
    // 관리자면 대시보드로 바로 이동
    if (driver.is_admin) {
      navigate('/admin');
      return;
    }
    
    // 오늘 이미 확인했는지 체크
    checkTodayStatus(token);
  }, [navigate]);

  useEffect(() => {
    if (alreadyConfirmed) return;
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        checkTodayStatus(token, false);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [alreadyConfirmed]);

  useEffect(() => {
    if (alreadyConfirmed || canStart || secondsUntilStart <= 0) return;
    const timer = setInterval(() => {
      setSecondsUntilStart((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) {
          setCanStart(true);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [alreadyConfirmed, canStart, secondsUntilStart]);

  useEffect(() => {
    if (alreadyConfirmed || !canStart || secondsUntilEnd <= 0) return;
    const timer = setInterval(() => {
      setSecondsUntilEnd((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) {
          setCanStart(false);
          setUnavailableReason('closed');
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [alreadyConfirmed, canStart, secondsUntilEnd]);

  const checkTodayStatus = async (token: string, showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await fetch(
        `${getApiBase()}/api/delivery-start/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDeliveryDate(data.delivery_date || '');
        setCanStart(Boolean(data.can_start));
        setDeliveryStartTime(data.delivery_start_time || '02:00');
        setDeliveryStartDayOffset(Number(data.delivery_start_day_offset || 0));
        setDeliveryStartEndTime(data.delivery_start_end_time || '10:00');
        setSecondsUntilStart(Number(data.seconds_until_start || 0));
        setSecondsUntilEnd(Number(data.seconds_until_end || 0));
        setUnavailableReason(data.unavailable_reason || '');
        setHasDelivery(Boolean(data.has_delivery));
        setDeliveryApartments(Array.isArray(data.delivery_apartments) ? data.delivery_apartments : []);
        setDeliveryTotalCount(Number(data.delivery_total_count || 0));
        if (data.confirmed) {
          setAlreadyConfirmed(true);
          setConfirmationTime(data.confirmation_time);
        }
      }
    } catch (err) {
      console.error('확인 상태 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartDelivery = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${getApiBase()}/api/delivery-start/confirm`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.is_new) {
          alert('✅ 배송 시작이 확인되었습니다!');
        }
        
        // 대시보드로 이동
        navigate('/dashboard');
      } else {
        const data = await response.json().catch(() => ({}));
        const detail = data.detail || {};
        const message = detail.message || '배송 시작 확인에 실패했습니다.';
        if (response.status === 423) {
          setCanStart(false);
          setSecondsUntilStart(Number(detail.seconds_until_start || 0));
          setSecondsUntilEnd(Number(detail.seconds_until_end || 0));
          setDeliveryStartTime(detail.delivery_start_time || deliveryStartTime);
          setDeliveryStartDayOffset(Number(detail.delivery_start_day_offset || deliveryStartDayOffset));
          setDeliveryStartEndTime(detail.delivery_start_end_time || deliveryStartEndTime);
          setUnavailableReason(detail.unavailable_reason || '');
          setHasDelivery(Boolean(detail.has_delivery));
          setDeliveryApartments(Array.isArray(detail.delivery_apartments) ? detail.delivery_apartments : deliveryApartments);
          setDeliveryTotalCount(Number(detail.delivery_total_count || 0));
        }
        alert(message);
      }
    } catch (err) {
      console.error('배송 시작 확인 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipToDashboard = () => {
    navigate('/dashboard');
  };

  const formatSeconds = (value: number) => {
    const safe = Math.max(0, value);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    }
    if (minutes > 0) {
      return `${minutes}분 ${seconds}초`;
    }
    return `${seconds}초`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('driverInfo');
    navigate('/login');
  };

  const getStartTitle = () => {
    if (!hasDelivery || unavailableReason === 'no_delivery') {
      return '오늘 배정된 배송이 없습니다';
    }
    if (unavailableReason === 'closed') {
      return '배송 시작 시간이 마감되었습니다';
    }
    return '배송을 시작하시겠습니까?';
  };

  const getStartDescription = () => {
    if (!hasDelivery || unavailableReason === 'no_delivery') {
      return '해당 날짜에 등록된 배송 차트가 없어 배송 시작을 할 수 없습니다.';
    }
    if (unavailableReason === 'closed') {
      return '관리자에게 강제 배송시작 처리를 요청해주세요.';
    }
    return '아래 버튼을 눌러 배송 시작을 확인해주세요.';
  };

  const getRestrictionMessage = () => {
    if (!hasDelivery || unavailableReason === 'no_delivery') {
      return '배송 내용 없음';
    }
    if (unavailableReason === 'closed') {
      return `시작 가능 시간이 ${deliveryStartEndTime}에 마감되었습니다`;
    }
    return '배송시작 시간 전입니다';
  };

  const getStartDayLabel = () => deliveryStartDayOffset === -1 ? '전날' : '당일';

  const getStartWindowLabel = () => `${getStartDayLabel()} ${deliveryStartTime}~당일 ${deliveryStartEndTime}`;

  const getButtonText = () => {
    if (loading) return '확인 중...';
    if (canStart) return '배송 시작';
    if (!hasDelivery || unavailableReason === 'no_delivery') return '배송 내용 없음';
    if (unavailableReason === 'closed') return `${deliveryStartEndTime} 마감됨`;
    return `${getStartDayLabel()} ${deliveryStartTime} 이후 시작 가능`;
  };

  if (loading) {
    return <div className="start-check-container">
      <div className="loading">확인 중...</div>
    </div>;
  }

  if (!driverInfo) {
    return null;
  }

  return (
    <div className="start-check-container">
      <div className="start-check-card">
        <div className="header">
          <h1>배송 시작 확인</h1>
          <p className="driver-name">{driverInfo.name}님</p>
        </div>

        <div className="date-info">
          <p className="today-date">{deliveryDate && new Date(deliveryDate + 'T00:00:00').toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
          })}</p>
        </div>

        {alreadyConfirmed ? (
          <div className="already-confirmed">
            <div className="status-icon">✅</div>
            <h2>배송 시작이 확인되었습니다</h2>
            <p className="confirm-time">
              확인 시각: {confirmationTime ? new Date(confirmationTime).toLocaleString('ko-KR') : '알 수 없음'}
            </p>
            <button 
              className="dashboard-btn" 
              onClick={handleSkipToDashboard}
            >
              배송 차트 보기 →
            </button>
          </div>
        ) : (
          <div className="not-confirmed">
            <h2>{getStartTitle()}</h2>
            <p className="description">
              {getStartDescription()}
            </p>

            {!canStart && (
              <div className={`time-restriction ${unavailableReason === 'no_delivery' ? 'no-delivery' : ''} ${unavailableReason === 'closed' ? 'closed' : ''}`}>
                <p className="restriction-message">{getRestrictionMessage()}</p>
                <p className="start-time-info">
                  시작 가능 시간: {getStartWindowLabel()}
                </p>
                {unavailableReason === 'before_start' && (
                  <p className="time-remaining">
                    남은 시간: {formatSeconds(secondsUntilStart)}
                  </p>
                )}
              </div>
            )}

            {canStart && (
              <p className="start-window-info">
                시작 마감까지 {formatSeconds(secondsUntilEnd)} 남았습니다.
              </p>
            )}
            
            <button 
              className="start-btn" 
              onClick={handleStartDelivery}
              disabled={loading || !canStart}
            >
              {getButtonText()}
            </button>
          </div>
        )}

        <div className="assigned-apartments">
          <h3>{hasDelivery ? `오늘 배송 아파트 (${deliveryTotalCount}건)` : '배정된 아파트'}</h3>
          <div className="apartment-list">
            {(hasDelivery && deliveryApartments.length > 0 ? deliveryApartments : driverInfo.assigned_apartments).map(apt => (
              <div key={apt} className="apartment-item">
                {apt}
              </div>
            ))}
          </div>
        </div>

        <div className="logout-section">
          <button onClick={handleLogout} className="logout-btn-bottom">로그아웃</button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryStartCheck;
